import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Camera, X, ArrowCounterClockwise, CheckCircle, Warning, PencilSimple, UploadSimple } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const overlay = {
  position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(3px)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000, padding: 16,
};

export default function ProofOfDeliveryModal({ trackingCode, onClose, onDone }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const sigCanvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasDrawnRef = useRef(false);
  const geoRef = useRef(null);

  const [photo, setPhoto] = useState(null);
  const [geoReady, setGeoReady] = useState(false);
  const [recipientName, setRecipientName] = useState("");
  const [cameraError, setCameraError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }, audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      setCameraError(true);
    }
  }, []);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [startCamera, stopCamera]);

  // Best-effort geolocation for the anti-fraud stamp (non-blocking)
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        geoRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy || 0 };
        setGeoReady(true);
      },
      () => { geoRef.current = null; },
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 30000 }
    );
  }, []);

  // ---- Image processing: resize + anti-fraud overlay + iterative compression ----
  const TARGET_BYTES = 200 * 1024;
  const MAX_DIM = 1000;

  const dataUrlBytes = (d) => {
    const i = d.indexOf(",");
    const b64 = i >= 0 ? d.slice(i + 1) : d;
    return Math.floor((b64.length * 3) / 4);
  };

  const drawStamp = (ctx, w, h) => {
    const now = new Date();
    const line1 = `${now.toLocaleString("pt-PT")}  \u00b7  ${trackingCode}`;
    const line2 = geoRef.current
      ? `Local: ${geoRef.current.lat.toFixed(5)}, ${geoRef.current.lng.toFixed(5)} (\u00b1${Math.round(geoRef.current.acc)}m)`
      : "Local: indisponivel";
    const barH = Math.max(48, Math.round(h * 0.13));
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(0, h - barH, w, barH);
    ctx.fillStyle = "#ffffff";
    ctx.textBaseline = "middle";
    const fs = Math.max(13, Math.round(w * 0.030));
    ctx.font = `600 ${fs}px sans-serif`;
    ctx.fillText(line1, 12, h - barH + barH * 0.33, w - 24);
    ctx.font = `500 ${Math.round(fs * 0.9)}px sans-serif`;
    ctx.fillText(line2, 12, h - barH + barH * 0.70, w - 24);
  };

  const processToDataUrl = (source, srcW, srcH) => {
    const scale = Math.min(1, MAX_DIM / Math.max(srcW, srcH));
    const w = Math.max(1, Math.round(srcW * scale));
    const h = Math.max(1, Math.round(srcH * scale));
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(source, 0, 0, w, h);
    drawStamp(ctx, w, h);
    let q = 0.7;
    let out = c.toDataURL("image/jpeg", q);
    while (dataUrlBytes(out) > TARGET_BYTES && q > 0.35) {
      q -= 0.1;
      out = c.toDataURL("image/jpeg", q);
    }
    return out;
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    setPhoto(processToDataUrl(video, video.videoWidth, video.videoHeight));
    stopCamera();
  };

  const retake = () => {
    setPhoto(null);
    startCamera();
  };

  const onFilePicked = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      setPhoto(processToDataUrl(img, img.naturalWidth || img.width, img.naturalHeight || img.height));
      URL.revokeObjectURL(url);
    };
    img.src = url;
    stopCamera();
  };

  // ---- Signature pad ----
  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };
  const startDraw = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const c = sigCanvasRef.current;
    const ctx = c.getContext("2d");
    const p = getPos(e, c);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const c = sigCanvasRef.current;
    const ctx = c.getContext("2d");
    const p = getPos(e, c);
    ctx.lineTo(p.x, p.y);
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    hasDrawnRef.current = true;
  };
  const endDraw = () => { drawingRef.current = false; };
  const clearSig = () => {
    const c = sigCanvasRef.current;
    if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
    hasDrawnRef.current = false;
  };

  const submit = async () => {
    setError("");
    if (!photo) { setError("Tire (ou carregue) uma foto da entrega."); return; }
    if (!hasDrawnRef.current) { setError("Recolha a assinatura do destinatario."); return; }
    if (!recipientName.trim()) { setError("Indique o nome de quem recebeu."); return; }
    const token = localStorage.getItem("blumove_token");
    const signature = sigCanvasRef.current.toDataURL("image/png");
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/shipments/${trackingCode}/proof-of-delivery`,
        { photo_base64: photo, signature_base64: signature, recipient_name: recipientName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onDone && onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao registar a entrega.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose} data-testid="pod-modal">
      <div
        onClick={(e) => e.stopPropagation()}
        className="animate-fade-in-up"
        style={{ background: "var(--surface)", borderRadius: 18, width: "100%", maxWidth: 460, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
      >
        <div className="flex items-center justify-between" style={{ padding: "16px 20px", borderBottom: "1px solid var(--border-color)" }}>
          <div>
            <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.05rem" }}>Prova de entrega</h3>
            <span style={{ fontFamily: "JetBrains Mono", fontSize: "0.75rem", color: "var(--primary)" }}>{trackingCode}</span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }} aria-label="Fechar">
            <X size={22} />
          </button>
        </div>

        <div style={{ padding: 20 }}>
          {/* Photo */}
          <span className="overline" style={{ display: "block", marginBottom: 6 }}>1. Foto da entrega</span>
          <div style={{ borderRadius: 12, overflow: "hidden", background: "#000", aspectRatio: "4/3", position: "relative", marginBottom: 10 }}>
            {photo ? (
              <img src={photo} alt="Prova" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : cameraError ? (
              <div className="flex flex-col items-center justify-center" style={{ height: "100%", color: "#cbd5e1", padding: 16, textAlign: "center" }}>
                <Warning size={28} weight="duotone" />
                <p style={{ fontSize: "0.8rem", marginTop: 8 }}>Camara indisponivel. Carregue uma foto.</p>
              </div>
            ) : (
              <video ref={videoRef} playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            )}
          </div>
          <div className="flex gap-2" style={{ marginBottom: 18 }}>
            {photo ? (
              <button data-testid="pod-retake-btn" onClick={retake} className="flex items-center justify-center gap-2" style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid var(--border-color)", background: "var(--bg)", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem" }}>
                <ArrowCounterClockwise size={16} /> Repetir
              </button>
            ) : (
              <>
                {!cameraError && (
                  <button data-testid="pod-capture-btn" onClick={capturePhoto} className="flex items-center justify-center gap-2" style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", background: "var(--primary)", color: "white", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem" }}>
                    <Camera size={16} weight="fill" /> Capturar
                  </button>
                )}
                <label className="flex items-center justify-center gap-2" style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid var(--border-color)", background: "var(--bg)", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem" }}>
                  <UploadSimple size={16} /> Carregar
                  <input type="file" accept="image/*" capture="environment" onChange={onFilePicked} style={{ display: "none" }} data-testid="pod-file-input" />
                </label>
              </>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 18, fontSize: "0.72rem", color: "var(--text-muted)" }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: geoReady ? "var(--success)" : "var(--text-muted)", flexShrink: 0 }} />
            {geoReady ? "Data/hora e localizacao serao gravadas na foto." : "Data/hora sera gravada na foto (localizacao indisponivel)."}
          </div>

          {/* Signature */}
          <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
            <span className="overline">2. Assinatura do destinatario</span>
            <button onClick={clearSig} className="flex items-center gap-1" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: "0.72rem", fontWeight: 600 }}>
              <PencilSimple size={13} /> Limpar
            </button>
          </div>
          <canvas
            ref={sigCanvasRef}
            data-testid="pod-signature-canvas"
            width={600}
            height={200}
            onPointerDown={startDraw}
            onPointerMove={moveDraw}
            onPointerUp={endDraw}
            onPointerLeave={endDraw}
            style={{ width: "100%", height: 140, borderRadius: 12, border: "1px dashed var(--border-color)", background: "#fff", touchAction: "none", cursor: "crosshair", marginBottom: 18 }}
          />

          {/* Recipient name */}
          <span className="overline" style={{ display: "block", marginBottom: 6 }}>3. Nome de quem recebeu</span>
          <input
            data-testid="pod-recipient-input"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            placeholder="Ex.: Maria Silva"
            style={{ width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid var(--border-color)", background: "var(--bg)", fontSize: "0.9rem", marginBottom: 16 }}
          />

          {error && (
            <div className="flex items-center gap-2" style={{ background: "rgba(220,38,38,0.08)", color: "#dc2626", padding: "9px 12px", borderRadius: 10, fontSize: "0.8rem", marginBottom: 14 }}>
              <Warning size={16} weight="fill" /> {error}
            </div>
          )}

          <button
            data-testid="pod-submit-btn"
            onClick={submit}
            disabled={submitting}
            className="flex items-center justify-center gap-2"
            style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: submitting ? "var(--text-muted)" : "var(--success)", color: "white", cursor: submitting ? "default" : "pointer", fontWeight: 700, fontSize: "0.92rem" }}
          >
            <CheckCircle size={18} weight="fill" /> {submitting ? "A registar..." : "Confirmar entrega"}
          </button>
        </div>
      </div>
    </div>
  );
}
